import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { ActionHandler } from './logic/ActionHandler';
import { toast, ToastContainer } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';


const ReviewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const estimateID = queryParams.get('estimateID');
  const token = queryParams.get('token');

  const [reviewed, setReviewed] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleReviewAction = async () => {
    if (!reviewNotes.trim()) {
      setError('Please provide revision notes');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await ActionHandler(
        estimateID,
        token,
        'review',
        reviewNotes
      );

      if (response.result) {
        setReviewed(true);
      } else {
        setError('Failed to submit for revision');
        toast.error(response.message, {
          duration: 5000,
          position: 'top-right',
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error('Error reviewing estimate:', error);
      setError(error.message || 'An error occurred while reviewing the estimate');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        textAlign: 'center',
        padding: 3
      }}>
        {reviewed ? (
          <>
            <RateReviewIcon color="primary" sx={{ fontSize: 80, mb: 3 }} />
            <Typography variant="h4" gutterBottom>
              Revision Requested
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Estimate #{estimateID} has been submitted for revision.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Return to Dashboard
            </Button>
          </>
        ) : (
          <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 'auto', textAlign: 'center', position: 'relative' }}>
            <IconButton 
              onClick={() => navigate('/dashboard')}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'text.secondary'
              }}
            >
              <CloseIcon />
            </IconButton>
           <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #23A26D 0%, #003A70 100%)'
            }}
          >
            <PublishedWithChangesOutlinedIcon sx={{ fontSize: 30, color: 'white' }} />
          </Box>
        </Box>
            <Typography variant="h4" gutterBottom>
              Revision Estimate #{estimateID}
            </Typography>
            <TextField
              label=" Please provide your revision notes:"
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              sx={{ mb: 3 }}
              error={!!error}
              helperText={error}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                color="main"
                onClick={handleReviewAction}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh',
      textAlign: 'center',
      padding: 3
    }}>
      {reviewed ? (
        <>
          <RateReviewIcon color="primary" sx={{ fontSize: 80, mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            Estimate Reviewed
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Estimate #{estimateID} has been submitted for revision.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 2 }}
          >
            Return to Dashboard
          </Button>
        </>
      ) : (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" gutterBottom>
            Revision Estimate #{estimateID}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please provide your revision notes:
          </Typography>
          <TextField
            label="Revision Notes"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{ mb: 3 }}
            error={!!error}
            helperText={error}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReviewAction}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Revision'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
    {/*<ToastContainer />*/}
    </>
  );
};

export default ReviewPage;